// frontend/src/pages/StoreSettingsPage.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const StoreSettingsPage = () => {
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchDefaultStore = async () => {
      try {
        const res = await axios.get('/api/stores');
        if (res.data.length === 0) {
          console.warn('No connected stores found for user.');
          return;
        }
        const defaultStore = res.data[0]; // or however you define “primary”
        setSelectedStoreId(defaultStore.id);
      } catch (err) {
        console.error('Failed to fetch user store', err);
      }
    };
    fetchDefaultStore();
  }, []);  

  useEffect(() => {
    if (!selectedStoreId) return;
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/settings/${selectedStoreId}`);
        
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to load settings', err);
        console.log('Fetching settings for storeId:', selectedStoreId);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [selectedStoreId]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedStoreId || !settings) return;
    setSaving(true);
  
    try {
      const payload = {
        ...settings,
        profitMargin: parseFloat(settings.profitMargin) || 1.0, // Fallback if empty
      };
  
      await axios.post(`/api/settings/${selectedStoreId}`, payload);
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings', err.response?.data || err.message);
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };
  

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4">Store One-Click Import Settings</h2>

      {loading || !settings ? (
        <p>Loading settings...</p>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-700">Profit Margin</label>
            <input
            type="number"
            step="5"
            min="0"
            max="300"
            value={settings.profitMargin ? (settings.profitMargin * 100).toFixed(0) : ''}
            onChange={(e) => {
                const val = parseFloat(e.target.value);
                handleChange('profitMargin', isNaN(val) ? null : val / 100);
            }}
            className="w-full border border-gray-300 rounded-md p-2"
            />
            <small className="text-gray-500">This defines your markup. 100% = 2× price.</small>
          </div>

          <div>
            <label className="block font-medium text-gray-700">Language</label>
            <select
              value={settings.language}
              onChange={(e) => handleChange('language', e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
            >
              <option value="en">English</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
              <option value="de">German</option>
              <option value="zh">Chinese</option>
            </select>
          </div>

          {['enrichWithGemini'].map(key => (
            <div key={key} className="flex items-center">
              <input
                type="checkbox"
                checked={settings[key]}
                onChange={(e) => handleChange(key, e.target.checked)}
                className="mr-2"
              />
              <label className="text-gray-700 capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
            </div>
          ))}

          {/* AI Image Generation */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">AI Image Generation</h3>
            <p className="text-sm text-gray-500 mb-3">
              Automatically generate brand-coherent lifestyle images using Gemini Batch API (50% cheaper, async).
            </p>

            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="autoImageQueue"
                  checked={!!settings.autoImageQueue}
                  onChange={(e) => handleChange('autoImageQueue', e.target.checked)}
                  className="mr-2 mt-1"
                />
                <label htmlFor="autoImageQueue" className="text-gray-700">
                  <span className="font-medium">Auto-queue on import</span>
                  <span className="block text-sm text-gray-500">
                    Scan new products for watermarks, Chinese text, or off-brand images and queue them automatically.
                  </span>
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="keepDraftUntilAI"
                  checked={settings.keepDraftUntilAI !== false}
                  onChange={(e) => handleChange('keepDraftUntilAI', e.target.checked)}
                  className="mr-2 mt-1"
                />
                <label htmlFor="keepDraftUntilAI" className="text-gray-700">
                  <span className="font-medium">Keep as Draft until AI images arrive</span>
                  <span className="block text-sm text-gray-500">
                    Queued products stay in Draft status; automatically published once AI images are uploaded.
                  </span>
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="deleteOriginalsAfterAI"
                  checked={settings.deleteOriginalsAfterAI !== false}
                  onChange={(e) => handleChange('deleteOriginalsAfterAI', e.target.checked)}
                  className="mr-2 mt-1"
                />
                <label htmlFor="deleteOriginalsAfterAI" className="text-gray-700">
                  <span className="font-medium">Delete original images after AI generation</span>
                  <span className="block text-sm text-gray-500">
                    Remove non-variant original images once AI-generated ones have been uploaded.
                  </span>
                </label>
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreSettingsPage;
